<script setup lang="ts">
import { type ButtonHTMLAttributes, computed } from 'vue'

import '@tix/ds/styles/portico/component/action-card.css'

export type ActionCardPadding = 'spacious' | 'comfy' | 'compact' | 'none'
export type ActionCardComponent = 'a' | 'button'

export type ActionCardProps = {
  /** Figma Padding axis */
  padding?: ActionCardPadding
  /** Figma State=Active — persistent selected look, not CSS :active */
  active?: boolean
  /** Figma State=Disabled */
  disabled?: boolean
  /** Root element. Use RouterLinkActionCard / ExternalLinkActionCard for navigation. */
  component?: ActionCardComponent
  /** Native button type. Bound only when `component` is `button`. */
  type?: ButtonHTMLAttributes['type']
}

const props = withDefaults(defineProps<ActionCardProps>(), {
  padding: 'spacious',
  component: 'button',
})

defineSlots<{
  default?: () => void
}>()

const isButton = computed(() => props.component === 'button')

const nativeType = computed(() => {
  if (!isButton.value) return props.type
  return props.type ?? 'button'
})

const nativeDisabled = computed(() => (isButton.value && props.disabled ? true : undefined))

const ariaDisabled = computed(() => {
  if (isButton.value || !props.disabled) return undefined
  return true
})

const tabIndex = computed(() => {
  if (isButton.value || !props.disabled) return undefined
  return -1
})

const onRootClick = (event: MouseEvent) => {
  if (props.disabled && !isButton.value) {
    event.preventDefault()
    event.stopImmediatePropagation()
  }
}
</script>

<template>
  <component
    :is="component"
    class="action-card"
    :class="[`padding-${padding}`, { active, disabled: disabled && !isButton }]"
    :type="nativeType"
    :disabled="nativeDisabled"
    :aria-disabled="ariaDisabled"
    :tabindex="tabIndex"
    @click="onRootClick"
  >
    <span class="content">
      <slot />
    </span>
  </component>
</template>

<style scoped>
.action-card {
  /* Figma Elevation/Shadow/Medium — not in generated Portico CSS */
  --action-card-elevation:
    0 42px 12px rgba(0, 0, 0, 0), 0 27px 11px rgba(0, 0, 0, 0), 0 15px 9px rgba(0, 0, 0, 0.01),
    0 7px 7px rgba(0, 0, 0, 0.02), 0 2px 4px rgba(0, 0, 0, 0.02);

  display: block;
  width: 100%;
  margin: 0;
  appearance: none;
  font: inherit;
  color: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  overflow: clip;
  background: var(--action-card-bg);
  border: var(--border-default) solid var(--action-card-border);
  border-radius: var(--radius-medium);
  box-shadow: var(--action-card-elevation);
  transition:
    background-color var(--speed-medium) ease-in-out,
    border-color var(--speed-medium) ease-in-out,
    box-shadow var(--speed-medium) ease-in-out;

  &.padding-spacious {
    padding: var(--padding-spacious);
  }

  &.padding-comfy {
    padding: var(--padding-comfy);
  }

  &.padding-compact {
    padding: var(--padding-compact);
  }

  &.padding-none {
    padding: 0;
  }

  &:hover:not(:disabled):not(.disabled):not(.active) {
    background: var(--action-card-bg-hover);
    border-color: var(--action-card-border-hover);
  }

  &:focus-visible:not(:disabled):not(.disabled) {
    outline: none;
    background: var(--action-card-bg-hover);
    border-color: var(--action-card-border-hover);
    box-shadow:
      var(--action-card-elevation),
      0 0 0 4px var(--form-input-border-focus-visible),
      0 0 0 6px var(--form-input-border-focus-visible-outline);
  }

  &.active {
    background: var(--action-card-bg-active);
    border-color: var(--action-card-border-active);
  }

  &.active:focus-visible:not(:disabled):not(.disabled) {
    background: var(--action-card-bg-active);
    border-color: var(--action-card-border-active);
  }

  &:disabled,
  &.disabled {
    background: var(--action-card-bg-disabled);
    border-color: var(--action-card-border);
    box-shadow: none;
    cursor: not-allowed;
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: var(--padding-comfy);
    width: 100%;
  }
}
</style>
